// The surfaces a page draws over or beside its content (#1890): a card, a
// drawer, a sheet and an inspector. Each was drawn by hand where it was first
// needed — the Equipment drawer (#1888), the Ports inspector (#1893), the
// switch's Review & apply sheet (#1891) and the topology's Findings drawer
// (#1442) — and each carried its own Escape. They are drawn here once, with
// their keyboard and focus behaviour, and `ui.tsx` exports them.
//
// **What each one is, and its role:**
// - a **card** is a titled section of a page, `<section>` named by its title;
//   one with a close control closes on Escape while focus is inside it;
// - a **drawer** is a non-modal `<aside>` over the right edge, named by its
//   label: the page behind it stays usable, so it takes no focus on opening;
// - a **sheet** is a modal dialog over a scrim, from the right edge, which
//   takes focus on opening; it is a `Dialog`;
// - a **dialog** is the browser's own `<dialog>`, opened with `showModal()`
//   (UX-002 of the 2026-11-01 review): the browser makes the page behind it
//   inert, so Tab cannot reach a control the dialog covers, and focus goes
//   back to its opener when it closes. Every modal surface is drawn on it.
// - an **inspector** is a `<section>` beside what was picked, closing on
//   Escape while focus is inside it.
//
// **Escape** is heard in the capture phase and claimed (`preventDefault`), so a
// surface closes before a page-wide listener — the topology's focus view —
// reads the same key, and a key already claimed is left alone. It is not
// heard from inside an open menu or an expanded control, which own their
// Escape first (`Popover`). **Focus returns** on closing to whatever held it
// when the surface opened, where focus would otherwise be lost with the
// surface.
import { useEffect, useId, useLayoutEffect, useRef, type ReactNode, type RefObject } from "react";
import { CloseIcon, Glyph } from "./icons";

/** Escape for a surface. `within` limits it to a key pressed while focus is
 * inside that element; without it the key is heard anywhere on the page. A
 * key from inside an open menu, or from a control whose `aria-expanded` is
 * true and which controls something other than this surface, is left to that
 * menu or control. */
export function useEscape(onClose: (() => void) | undefined, { within, own, enabled = true }: { within?: RefObject<HTMLElement | null>; own?: string; enabled?: boolean } = {}) {
  const close = useRef(onClose);
  useLayoutEffect(() => { close.current = onClose; });
  const has = onClose !== undefined;
  useEffect(() => {
    if (!enabled || !has) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const t = e.target instanceof Element ? e.target : undefined;
      if (within && !(t && within.current?.contains(t))) return;
      if (t?.closest(".popover")) return;
      const expanded = t?.closest("[aria-expanded=true]");
      if (expanded && (!own || expanded.getAttribute("aria-controls") !== own)) return;
      e.preventDefault();
      close.current?.();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [enabled, has, within, own]);
}

/** Returns focus, when the surface closes, to what held it when it opened —
 * where focus is still inside the surface or has fallen to the page's body
 * with it, and never where an operator has since moved it elsewhere. */
export function useReturnFocus(surface: RefObject<HTMLElement | null>) {
  useLayoutEffect(() => {
    const opener = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : undefined;
    const node = surface.current;
    return () => {
      const now = document.activeElement;
      const lost = !now || now === document.body || (node?.contains(now) ?? false);
      if (lost && opener?.isConnected) opener.focus();
    };
  }, [surface]);
}

/** A titled section of a page. `onClose` gives it a close control, Escape
 * while focus is inside it, and the return of focus. */
export function Card({ title, head, onClose, closeLabel = "Close", className, children }: {
  title?: ReactNode;
  /** Anything else in the card's head, after the title: a count, an act. */
  head?: ReactNode;
  onClose?(): void;
  /** The close control's accessible name. */
  closeLabel?: string;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  const titleId = useId();
  useEscape(onClose, { within: ref });
  useReturnFocus(ref);
  return (
    <section ref={ref} className={className ? `card ${className}` : "card"} aria-labelledby={title ? titleId : undefined}>
      {(title || head || onClose) && (
        <div className="card-head">
          {title && <h2 id={titleId}>{title}</h2>}
          {head}
          {onClose && <button type="button" className="btn btn-quiet btn-sm" aria-label={closeLabel} onClick={onClose}><Glyph icon={CloseIcon} /></button>}
        </div>
      )}
      {children}
    </section>
  );
}

/** A non-modal panel over the right edge: the page behind stays usable, so it
 * takes no focus when it opens, and Escape anywhere closes it. `inset` draws
 * it inside its positioned parent (the topology's drawing) rather than over
 * the window. */
export function Drawer({ id, label, title, onClose, closeLabel = "Close", inset = false, className, children }: {
  id?: string;
  /** The `<aside>`'s accessible name. */
  label: string;
  title?: ReactNode;
  onClose(): void;
  closeLabel?: string;
  inset?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  useEscape(onClose, { own: id });
  useReturnFocus(ref);
  const classes = ["drawer", inset ? "is-inset" : "", className ?? ""].filter(Boolean).join(" ");
  return (
    <aside ref={ref} id={id} className={classes} aria-label={label}>
      <div className="drawer-head">
        {title && <h2>{title}</h2>}
        <button type="button" className="btn btn-sm btn-quiet" onClick={onClose}>{closeLabel}</button>
      </div>
      {children}
    </aside>
  );
}

/** A modal dialog on the browser's own `<dialog>` (UX-002). `showModal()`
 * puts it in the top layer and makes the rest of the document inert, so focus
 * cannot leave it by Tab or by the pointer; a Tab past its last control wraps
 * to its first, as the dialog pattern asks. It takes focus on opening —
 * `initialFocus`, else the dialog itself — without scrolling, and gives it
 * back to whatever held it when it closes. Escape closes it, and a click on
 * its backdrop where `backdropCloses`, unless `busy`.
 *
 * Where the browser has no `showModal` (the test DOM), it is opened with its
 * `open` attribute and the Tab wrap is what holds focus inside it. */
export function Dialog({ label, role, className, onClose, busy = false, backdropCloses = false, initialFocus, dialogRef, children }: {
  /** The dialog's accessible name. */
  label: string;
  /** `alertdialog` for a question that interrupts a navigation and must be answered. */
  role?: "dialog" | "alertdialog";
  className?: string;
  onClose(): void;
  /** While a write is in flight: Escape and the backdrop do nothing. */
  busy?: boolean;
  backdropCloses?: boolean;
  /** The control focus opens on, such as *Cancel* (ADR-0436 decision 3a (iii)). */
  initialFocus?: RefObject<HTMLElement | null>;
  /** The dialog element, for a caller that moves focus inside it. */
  dialogRef?: RefObject<HTMLElement | null>;
  children: ReactNode;
}) {
  const own = useRef<HTMLDialogElement>(null);
  const ref = (dialogRef ?? own) as RefObject<HTMLDialogElement | null>;
  useReturnFocus(ref);
  useEscape(onClose, { enabled: !busy });
  const latest = useRef({ onClose, busy, backdropCloses });
  useLayoutEffect(() => { latest.current = { onClose, busy, backdropCloses }; });
  useLayoutEffect(() => {
    const d = ref.current;
    if (!d) return;
    // The browser's own Escape is a `cancel`: Zenith closes through `onClose`, so the
    // dialog never shuts behind React's back; one the browser closes anyway is reported.
    const onCancel = (e: Event) => e.preventDefault();
    // A `close` the browser queued for a dialog that has been shown again since is stale:
    // React's development double mount closes and reopens it, and the event lands on the
    // reopened dialog, which would shut it as it appeared (#2344). Only a dialog that is
    // closed reports a close.
    const onShut = () => { if (!d.open) latest.current.onClose(); };
    // A Tab past the last control wraps to the first, and Shift+Tab before the first to the last.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const stops = [...d.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => !el.closest("[inert]"));
      if (stops.length === 0) { e.preventDefault(); return; }
      const first = stops[0]!, last = stops[stops.length - 1]!;
      const at = document.activeElement;
      if (e.shiftKey && (at === first || at === d)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && at === last) { e.preventDefault(); first.focus(); }
    };
    // A click on the dialog element itself is on its padding or on the backdrop
    // around it; only the second closes it, and only where asked.
    const onClick = (e: MouseEvent) => {
      const { onClose: close, busy: held, backdropCloses: closes } = latest.current;
      if (!closes || held || e.target !== d) return;
      const r = d.getBoundingClientRect();
      if (r.width === 0 || e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) close();
    };
    d.addEventListener("cancel", onCancel);
    d.addEventListener("keydown", onKey);
    d.addEventListener("click", onClick);
    if (typeof d.showModal === "function") {
      if (!d.open) d.showModal();
      d.addEventListener("close", onShut);
    } else {
      d.setAttribute("open", "");
    }
    (initialFocus?.current ?? d).focus({ preventScroll: true });
    d.scrollTop = 0;
    return () => {
      d.removeEventListener("cancel", onCancel);
      d.removeEventListener("keydown", onKey);
      d.removeEventListener("click", onClick);
      d.removeEventListener("close", onShut);
      if (typeof d.close === "function" && d.open) d.close();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- opened once; the page re-renders while it is open
  return (
    <dialog ref={ref} role={role} className={className ? `ui-dialog ${className}` : "ui-dialog"} aria-label={label} aria-modal="true" tabIndex={-1}>
      {children}
    </dialog>
  );
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/** A modal dialog from the right edge, over the backdrop. It takes focus when
 * it opens and gives it back when it closes; Escape and the backdrop close it
 * unless `busy`, while a write is in flight. */
export function Sheet({ label, title, onClose, busy = false, sheetRef, strip, foot, children }: {
  /** The dialog's accessible name. */
  label: string;
  title: ReactNode;
  onClose(): void;
  busy?: boolean;
  /** The dialog element, for a caller that moves focus inside it. */
  sheetRef?: RefObject<HTMLElement | null>;
  /** Between the head and the body: the steps a sheet walks through. */
  strip?: ReactNode;
  /** The row of controls under the body. */
  foot?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Dialog label={label} className="sheet" onClose={onClose} busy={busy} backdropCloses dialogRef={sheetRef}>
      <div className="sheet-head">
        <h2>{title}</h2>
        <button type="button" className="btn btn-quiet" onClick={onClose} disabled={busy} aria-label="Close"><Glyph icon={CloseIcon} /></button>
      </div>
      {strip}
      <div className="sheet-body">{children}</div>
      {foot && <div className="sheet-foot">{foot}</div>}
    </Dialog>
  );
}

/** The panel beside what was picked: a heading naming it, a close control,
 * and Escape while focus is inside it. */
export function Inspector({ title, label = "Inspector", onClose, className, children }: {
  title: ReactNode;
  label?: string;
  onClose(): void;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  useEscape(onClose, { within: ref });
  useReturnFocus(ref);
  return (
    <section ref={ref} className={className ? `panel inspector ${className}` : "panel inspector"} aria-label={label}>
      <div className="page-head">
        <h2>{title}</h2>
        <button type="button" className="btn btn-quiet" onClick={onClose}>Close</button>
      </div>
      {children}
    </section>
  );
}
